import React, { useEffect, useRef, useState } from "react";
import {
  formatJustificationLineRate,
  getNonL1JustificationNote,
} from "./awardJustificationUtils";

const EMPTY_SELECTED_OPTIONS = [];

const OptionIcon = ({ option }) => {
  const normalizedOption = String(option || "").trim();
  const commonProps = {
    className: "h-3.5 w-3.5 text-slate-400",
    fill: "none",
    stroke: "currentColor",
    viewBox: "0 0 24 24",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  const icons = {
    "Single Quotation": (
      <svg {...commonProps}>
        <path d="M8 4h8l3 3v13H5V4h3Z" />
        <path d="M15 4v4h4" />
        <path d="M8 12h8" />
        <path d="M8 16h5" />
      </svg>
    ),
    "Client Approval": (
      <svg {...commonProps}>
        <path d="M16 11V7a4 4 0 0 0-8 0v4" />
        <rect x="5" y="11" width="14" height="9" rx="2" />
        <path d="m9 15 2 2 4-4" />
      </svg>
    ),
    "Management Approval": (
      <svg {...commonProps}>
        <path d="M4 20h16" />
        <path d="M6 20V9l6-4 6 4v11" />
        <path d="M9 20v-6h6v6" />
        <path d="m9 10 2 2 4-4" />
      </svg>
    ),
    "Delivery / Completion Timeline": (
      <svg {...commonProps}>
        <path d="M4 7h10v10H4z" />
        <path d="M14 10h3l3 3v4h-6" />
        <path d="M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
        <path d="M17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
      </svg>
    ),
    "Payment Terms": (
      <svg {...commonProps}>
        <rect x="4" y="6" width="16" height="12" rx="2" />
        <path d="M4 10h16" />
        <path d="M8 14h3" />
        <path d="M15 14h1" />
      </svg>
    ),
    "Credit Capacity": (
      <svg {...commonProps}>
        <path d="M5 9h14" />
        <path d="M7 9V6h10v3" />
        <rect x="4" y="9" width="16" height="10" rx="2" />
        <path d="M8 14h8" />
      </svg>
    ),
    "Material/ Workmanship Quality": (
      <svg {...commonProps}>
        <path d="M12 3 4 7l8 4 8-4-8-4Z" />
        <path d="m4 12 8 4 8-4" />
        <path d="m4 17 8 4 8-4" />
      </svg>
    ),
    "Material / Workmanship Quality": (
      <svg {...commonProps}>
        <path d="M12 3 4 7l8 4 8-4-8-4Z" />
        <path d="m4 12 8 4 8-4" />
        <path d="m4 17 8 4 8-4" />
      </svg>
    ),
    "After-Sales Service": (
      <svg {...commonProps}>
        <path d="M12 20a8 8 0 1 0-8-8" />
        <path d="M4 20v-5h5" />
        <path d="m9 12 2 2 4-5" />
      </svg>
    ),
    "Technical Compliance": (
      <svg {...commonProps}>
        <path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6l-7-3Z" />
        <path d="m9 12 2 2 4-5" />
      </svg>
    ),
    "Warranty / Guarantee Terms": (
      <svg {...commonProps}>
        <circle cx="12" cy="9" r="5" />
        <path d="m9 14-1 7 4-2 4 2-1-7" />
        <path d="m10 9 1.5 1.5L15 7" />
      </svg>
    ),
  };

  return icons[normalizedOption] || (
    <svg {...commonProps}>
      <circle cx="12" cy="12" r="8" />
      <path d="m9 12 2 2 4-5" />
    </svg>
  );
};

const AwardJustificationModal = ({
  open,
  options = [],
  context = {},
  initialSelectedOptions = EMPTY_SELECTED_OPTIONS,
  initialRemark = "",
  loading = false,
  submitLabel = "Submit & send for approval",
  onCancel,
  onSubmit,
}) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [remark, setRemark] = useState("");
  const [remarkError, setRemarkError] = useState("");
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setSelectedOptions(Array.isArray(initialSelectedOptions) ? initialSelectedOptions : []);
      setRemark(String(initialRemark || ""));
      setRemarkError("");
    }
    wasOpenRef.current = open;
  }, [open, initialSelectedOptions, initialRemark]);

  if (!open) return null;

  const toggleOption = (option) => {
    setSelectedOptions((prev) =>
      prev.includes(option) ? prev.filter((item) => item !== option) : [...prev, option]
    );
  };

  const handleSubmit = () => {
    const trimmedRemark = remark.trim();
    if (!selectedOptions.length || loading) return;
    if (!trimmedRemark) {
      setRemarkError("Remark is required for Non-L1 justification.");
      return;
    }
    onSubmit?.(selectedOptions, trimmedRemark);
  };

  const impactedLineCount = context?.lines?.length || 0;
  const impactedVendorCount = context?.selectedSuppliers?.length || 0;
  const isSingleQuotation = context?.triggerType === "SINGLE_QUOTATION";
  const lineItemColumnLabel = context?.rfqType === "contractor" ? "Scope of work" : "Line item";
  const affectedVendorGroups = (context?.lines || []).reduce((groups, line) => {
    const key = String(line?.supplierId || line?.supplierName || "vendor");
    const existingGroup = groups.find((group) => group.key === key);
    if (existingGroup) {
      existingGroup.lines.push(line);
      return groups;
    }
    groups.push({
      key,
      supplierName: line?.supplierName || "Selected vendor",
      supplierRank: line?.supplierRank || "",
      lines: [line],
    });
    return groups;
  }, []);

  return (
    <div className="fixed inset-0 z-[1000] isolate flex items-center justify-center bg-black/45 px-4 py-5">
      <div className={`flex max-h-[calc(100vh-40px)] w-full flex-col overflow-hidden rounded-xl bg-white shadow-2xl ${isSingleQuotation ? "max-w-2xl" : "max-w-6xl"}`}>
        <div className="shrink-0 border-b border-slate-200 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-slate-950">{context?.title || "Award justification required"}</h3>
              <p className="mt-1 text-sm text-slate-600">{context?.summary}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {!isSingleQuotation && (
                  <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700 ring-1 ring-orange-100">
                    {impactedLineCount || "Multiple"} affected line{impactedLineCount === 1 ? "" : "s"}
                  </span>
                )}
                {impactedVendorCount > 0 && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                    {isSingleQuotation ? "Single vendor quotation" : `${impactedVendorCount} vendor${impactedVendorCount === 1 ? "" : "s"} need reason`}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="rounded-md px-2 py-1 text-sm text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Close
            </button>
          </div>
          <div className="mt-2 rounded-lg border border-orange-100 bg-orange-50/40 p-1">
            <label className="text-sm font-semibold text-slate-900">Remark <span className="text-red-500">*</span></label>
            <textarea
              value={remark}
              onChange={(event) => {
                setRemark(event.target.value);
                if (remarkError) setRemarkError("");
              }}
              rows={3}
              disabled={loading}
              className="mt-2 w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 disabled:cursor-not-allowed disabled:bg-slate-100"
              placeholder="Add mandatory remark for selecting Non-L1 vendor"
            />
            {remarkError ? <p className="mt-2 text-xs font-medium text-red-600">{remarkError}</p> : null}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {isSingleQuotation && context?.selectedSuppliers?.length > 0 && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Selected vendor</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {context.selectedSuppliers.map((supplier) => (
                  <span
                    key={supplier?.supplierId || supplier?.businessName}
                    className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-900 ring-1 ring-amber-200"
                  >
                    {supplier?.businessName || "Selected vendor"}{supplier?.rank ? ` (${supplier.rank})` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className={`grid gap-4 ${isSingleQuotation ? "" : "lg:grid-cols-[1.22fr_0.78fr]"}`}>
            {!isSingleQuotation && (
              <div className="min-w-0">
              {context?.lines?.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Affected lines</p>
                    <p className="text-[11px] text-slate-500">Scroll to review all {impactedLineCount} lines</p>
                  </div>
                  <div className="max-h-[min(42vh,360px)] overflow-y-auto overflow-x-hidden">
                    <table className="w-full table-fixed text-xs">
                      <colgroup>
                        <col className="w-[30%]" />
                        <col className="w-[34%]" />
                        <col className="w-[18%]" />
                        <col className="w-[18%]" />
                      </colgroup>
                      <thead className="sticky top-0 z-10 bg-slate-100 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-4 py-2 text-left">Vendor</th>
                          <th className="px-4 py-2 text-left">{lineItemColumnLabel}</th>
                          <th className="px-4 py-2 text-left">Lowest rate</th>
                          <th className="px-4 py-2 text-left">Selected rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {affectedVendorGroups.map((group) =>
                          group.lines.map((line, index) => (
                            <tr key={`${group.key}-${line.itemName}-${index}`} className="border-t border-slate-100 align-top">
                              {index === 0 && (
                                <td rowSpan={group.lines.length} className="px-4 py-3">
                                  <div className="min-w-0">
                                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                      <span className="font-semibold text-slate-900" title={group.supplierName}>
                                        {group.supplierName}
                                      </span>
                                      {group.supplierRank ? (
                                        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                                          {group.supplierRank}
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                              )}
                              <td className="px-4 py-3">
                                <p className="break-words font-medium text-slate-900" title={line.itemName}>
                                  {line.itemName}
                                </p>
                              </td>
                              <td className="px-4 py-3 font-medium text-emerald-700">
                                Rs. {formatJustificationLineRate(line.lowestRate)}
                              </td>
                              <td className="px-4 py-3 font-medium text-amber-700">
                                Rs. {formatJustificationLineRate(line.selectedRate)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            )}

            <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Select justification</p>
                  <p className="mt-0.5 text-xs text-slate-500">Choose one or more reasons for this award decision.</p>
                </div>
                <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                  {selectedOptions.length} selected
                </span>
              </div>
              <div className={`grid max-h-[min(44vh,380px)] gap-2 overflow-y-auto pr-1 ${isSingleQuotation ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-1"}`}>
                {options.map((option) => {
                  const selected = selectedOptions.includes(option);
                  const optionNote = getNonL1JustificationNote(option);
                  return (
                    <button
                      type="button"
                      key={option}
                      onClick={() => toggleOption(option)}
                      disabled={loading}
                      className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm transition ${
                        selected
                          ? "border-orange-400 bg-orange-50 text-orange-800"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                      } disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      <span className={`mt-[3px] h-4 w-4 shrink-0 rounded border ${selected ? "border-orange-500 bg-orange-500" : "border-slate-300"}`}>
                        {selected ? <span className="block text-center text-[10px] leading-4 text-white">{"\u2713"}</span> : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5 font-normal leading-5">
                          <span className="shrink-0">
                            <OptionIcon option={option} />
                          </span>
                          <span>{option}</span>
                        </span>
                        {optionNote ? (
                          <span className={`block pl-5 text-[11px] font-normal leading-3.5 ${selected ? "text-orange-700" : "text-slate-500"}`}>
                            {optionNote}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-6 py-3">
          <p className="text-xs text-slate-500">
            This stores reason only. RFQ rates, approval levels, and calculations remain unchanged.
          </p>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || selectedOptions.length === 0 || !remark.trim()}
              className="rounded-md bg-black px-4 py-2 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Submitting..." : submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AwardJustificationModal;
